import React from 'react';

const ApiDocs = () => {
    const endpoints = [
        {
            method: 'POST',
            path: '/api/auth/login',
            description: 'Authenticate user and get JWT token',
            example: `{
  "email": "user@example.com",
  "password": "yourpassword"
}`
        },
        {
            method: 'POST',
            path: '/api/heygen/videos/generate',
            description: 'Generate a video with AI avatar',
            auth: true,
            example: `{
  "text": "Hello, this is my AI avatar!",
  "avatar_id": "avatar_123",
  "voice_id": "voice_456",
  "background_type": "color",
  "background_value": "#ffffff"
}`
        },
        {
            method: 'GET',
            path: '/api/heygen/avatars',
            description: 'Get list of available avatars',
            auth: true
        },
        {
            method: 'GET',
            path: '/api/heygen/voices',
            description: 'Get list of available voices',
            auth: true
        },
        {
            method: 'GET',
            path: '/api/users/jobs',
            description: 'Get user\'s video generation jobs',
            auth: true
        }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">API Documentation</h1>
                    <p className="text-gray-600">
                        Integrate HeyGen video generation into your applications
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Navigation */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl shadow-md border p-6 sticky top-4">
                            <h2 className="font-semibold text-gray-900 mb-4">Quick Navigation</h2>
                            <nav className="space-y-2">
                                <a href="#getting-started" className="block text-blue-600 hover:text-blue-500">
                                    Getting Started
                                </a>
                                <a href="#authentication" className="block text-blue-600 hover:text-blue-500">
                                    Authentication
                                </a>
                                <a href="#endpoints" className="block text-blue-600 hover:text-blue-500">
                                    API Endpoints
                                </a>
                                <a href="#examples" className="block text-blue-600 hover:text-blue-500">
                                    Code Examples
                                </a>
                                <a href="#webhooks" className="block text-blue-600 hover:text-blue-500">
                                    Webhooks
                                </a>
                            </nav>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Getting Started */}
                        <section id="getting-started" className="bg-white rounded-xl shadow-md border p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">🚀 Getting Started</h2>
                            <div className="space-y-4">
                                <p className="text-gray-600">
                                    The HeyGen Integration API allows you to programmatically generate AI videos,
                                    manage avatars and voices, and integrate video generation into your applications.
                                </p>

                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <h3 className="font-medium text-blue-900 mb-2">Base URL</h3>
                                    <code className="text-sm text-blue-700">
                                        {window.location.origin}/api
                                    </code>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-medium text-gray-900 mb-2">Content Type</h3>
                                    <p className="text-sm text-gray-600">
                                        All requests should use <code>Content-Type: application/json</code>
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Authentication */}
                        <section id="authentication" className="bg-white rounded-xl shadow-md border p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">🔐 Authentication</h2>
                            <div className="space-y-4">
                                <p className="text-gray-600">
                                    Most API endpoints require authentication using JWT tokens. Include the token
                                    in the Authorization header:
                                </p>

                                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm">
                                    <div className="text-green-400"># Login to get token</div>
                                    <div className="mt-2">
                                        curl -X POST {window.location.origin}/api/auth/login \<br />
                                        &nbsp;&nbsp;-H "Content-Type: application/json" \<br />
                                        &nbsp;&nbsp;-d '{"{"}"email": "user@example.com", "password": "yourpassword"{"}"}'
                                    </div>

                                    <div className="text-green-400 mt-4"># Use token in requests</div>
                                    <div className="mt-2">
                                        curl -X GET {window.location.origin}/api/heygen/avatars \<br />
                                        &nbsp;&nbsp;-H "Authorization: Bearer YOUR_JWT_TOKEN"
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* API Endpoints */}
                        <section id="endpoints" className="bg-white rounded-xl shadow-md border p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 API Endpoints</h2>
                            <div className="space-y-4">
                                {endpoints.map((endpoint, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <span className={`px-2 py-1 text-xs font-medium rounded ${endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                                                    endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-gray-100 text-gray-800'
                                                }`}>
                                                {endpoint.method}
                                            </span>
                                            <code className="text-sm font-mono text-gray-700">
                                                {endpoint.path}
                                            </code>
                                            {endpoint.auth && (
                                                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                                                    🔒 Auth Required
                                                </span>
                                            )}
                                        </div>

                                        <p className="text-gray-600 text-sm mb-3">{endpoint.description}</p>

                                        {endpoint.example && (
                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Example Request Body:</h4>
                                                <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                                                    <code>{endpoint.example}</code>
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Code Examples */}
                        <section id="examples" className="bg-white rounded-xl shadow-md border p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">💻 Code Examples</h2>

                            <div className="space-y-6">
                                {/* JavaScript Example */}
                                <div>
                                    <h3 className="font-medium text-gray-900 mb-2">JavaScript (Fetch API)</h3>
                                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                                        <code>{`// Login and generate video
const response = await fetch('/api/auth/login', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'user@example.com',
    password: 'yourpassword'
  })
});

const { token } = await response.json();

// Generate video
const videoResponse = await fetch('/api/heygen/videos/generate', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': \`Bearer \${token}\`
  },
  body: JSON.stringify({
    text: 'Hello from my AI avatar!',
    avatar_id: 'avatar_123',
    voice_id: 'voice_456'
  })
});

const videoData = await videoResponse.json();
console.log('Video job started:', videoData);`}</code>
                                    </pre>
                                </div>

                                {/* Python Example */}
                                <div>
                                    <h3 className="font-medium text-gray-900 mb-2">Python (requests)</h3>
                                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                                        <code>{`import requests

# Login
login_response = requests.post('${window.location.origin}/api/auth/login', json={
    'email': 'user@example.com',
    'password': 'yourpassword'
})

token = login_response.json()['token']

# Generate video
video_response = requests.post(
    '${window.location.origin}/api/heygen/videos/generate',
    headers={'Authorization': f'Bearer {token}'},
    json={
        'text': 'Hello from my AI avatar!',
        'avatar_id': 'avatar_123',
        'voice_id': 'voice_456'
    }
)

video_data = video_response.json()
print('Video job started:', video_data)`}</code>
                                    </pre>
                                </div>
                            </div>
                        </section>

                        {/* Webhooks */}
                        <section id="webhooks" className="bg-white rounded-xl shadow-md border p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">🔗 Webhooks</h2>
                            <div className="space-y-4">
                                <p className="text-gray-600">
                                    Get real-time notifications when your video generation jobs are completed.
                                </p>

                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <h3 className="font-medium text-blue-900 mb-2">Webhook URL</h3>
                                    <code className="text-sm text-blue-700">
                                        {window.location.origin}/api/webhooks/heygen
                                    </code>
                                    <p className="text-xs text-blue-600 mt-1">
                                        Configure this URL in your HeyGen dashboard
                                    </p>
                                </div>

                                <div>
                                    <h3 className="font-medium text-gray-900 mb-2">Webhook Payload Example:</h3>
                                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                                        <code>{`{
  "event_type": "avatar.video.generate.completed",
  "data": {
    "video_id": "video_123",
    "video_url": "https://cdn.heygen.com/video_123.mp4",
    "status": "completed",
    "callback_id": "your_callback_id"
  }
}`}</code>
                                    </pre>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApiDocs;